/**
 * canvasTicket.ts — Renderiza el ticket de pedido BLE a un <canvas> 2D (imagen)
 * en lugar de texto ESC/POS plano.
 *
 * Motivación: el encoder de texto (`buildWebBleTicket.ts`) manda el ticket usando
 * la fuente interna de la térmica → se ve tosco y sólo el título cambia de tamaño.
 * pdfMake (web) y Electron rasterizan HTML a imagen y la mandan como raster
 * (GS v 0). Aquí dibujamos el MISMO contenido/orden que el ticket Electron en un
 * canvas y devolvemos el `HTMLCanvasElement`; el caller lo empaqueta a GS v 0 y lo
 * imprime. Así el ticket BLE se ve IGUAL, soporta los 5 niveles de tamaño por
 * elemento (título/subtítulo/cuerpo), logo y ñ/acentos perfectos (los dibuja el
 * navegador, sin code-pages).
 *
 * BEST-EFFORT: si no hay `document`/`Image` (jsdom) o algo falla (logo tainted por
 * CORS, etc.) devuelve `null` y el caller usa el fallback de texto. NUNCA se accede
 * a `document`/`Image`/`canvas` en el top-level.
 *
 * El CONTENIDO (condiciones, etiquetas, totales) replica exactamente la lógica del
 * encoder de texto (`buildWebBleTicket.ts`) y del ticket Electron
 * (`pages/Orders/utils/electronTicket/buildTicket.ts`): mismos helpers de formato,
 * mismos mapas, `computeOrderTotal` para "Total a pagar" y "Cambio".
 */

import { OrderDetail } from '@interfaces/OrderTypes';
import { PRINT_TEXT_SIZES_VALUES_MAP } from '@utils/constants';
import { dotsForWidth, loadDrawableImage } from './raster';
import { bodySize, separatorCols, titleSize, WebBleTicketType } from './buildWebBleTicket';
import { buildTicketModel, TicketContentConfig } from './ticketModel';
import type { TicketContentFlags } from '@store/slices/webBlePrinters.types';

/** Roles de tamaño disponibles por nivel (mismas keys que el ticket Electron). */
type TextRole = keyof (typeof PRINT_TEXT_SIZES_VALUES_MAP)['medium'];

export type CanvasTicketOptions = {
  /** Tipo de ticket: completo con precios o comanda de cocina simplificada. */
  ticketType: WebBleTicketType;
  /** Tamaño de texto (5 niveles): 'xxs' | 'small' | 'medium' | 'large' | 'xxl'. */
  textSize: keyof typeof PRINT_TEXT_SIZES_VALUES_MAP;
  /** Ancho del rollo en mm — define el ancho del canvas en puntos (384 / 576). */
  pageWidth?: 58 | 80;
  /** Si es false, omite las líneas separadoras decorativas. */
  decoration?: boolean;
  /** Si es false, no se intenta dibujar el logo. */
  showLogo?: boolean;
  /** Datos del comercio para el footer (sólo ticket normal). */
  commerceName?: string;
  commerceAddress?: string;
  /** URL del logo del comercio (CDN). Se carga vía fetch+createImageBitmap (sin depender del CORS del canvas). */
  logoUrl?: string | null;
  /** Etiqueta personalizada para el campo de referencias de dirección. */
  referencesLabel?: string;
  /** Flags de visibilidad — paridad con el ticket Electron. */
  showClientPhone?: boolean;
  /** Imprimir el NOMBRE del comercio. Aplica a caja Y cocina. Default true. */
  showCommerceName?: boolean;
  /** Imprimir la DIRECCIÓN del comercio (solo caja). Default true. */
  showCommerceAddress?: boolean;
  /**
   * Flags de contenido RESUELTOS (showPrices/showTotals/showTip/…). OPCIONAL: cuando se
   * omite, el IR usa el preset del rol + los show* legacy → preview byte/visualmente
   * idéntico al histórico. Cuando se pasa (config rica de la UI), el preview de imagen
   * HONRA estos flags igual que la impresión — preview == impresión.
   */
  content?: TicketContentFlags;
  /** Texto del aviso legal del footer (editable por el usuario). Si se omite, el default. */
  legalDisclaimer?: string;
};

// Factor de escala de los px HTML al lienzo en puntos. Los px de
// PRINT_TEXT_SIZES_VALUES_MAP están pensados para render HTML (~302pt de ancho útil
// @80mm en pdfMake). Para llenar el rollo de 576/384 dots escalamos proporcional al
// ancho: cada nivel se ve igual de "lleno" en ambos rollos.
const HTML_REFERENCE_WIDTH_PT = 200;

// Margen lateral del lienzo (en puntos).
const SIDE_PADDING = 8;
// Separación vertical extra entre líneas (en puntos, antes de escalar la fuente).
const LINE_GAP = 4;
// Logo: lado máximo en puntos (el Electron usa 110px; lo dibujamos centrado).
const LOGO_MAX_SIDE = 120;

// Alineación de una línea de texto.
type Align = 'left' | 'center' | 'right';

// Una línea simple de texto a dibujar.
type TextLine = {
  kind: 'text';
  text: string;
  role: TextRole;
  align: Align;
  bold: boolean;
};

// Una fila "etiqueta ......... valor" (nombre a la izq, precio a la derecha).
type RowLine = {
  kind: 'row';
  left: string;
  right: string;
  role: TextRole;
  bold: boolean;
  /** Sangría del texto izquierdo en puntos (extras indentados). */
  indent?: number;
};

// Una línea separadora horizontal (decoración).
type SepLine = { kind: 'sep' };

// Espacio vertical en blanco (en puntos, sin escalar).
type FeedLine = { kind: 'feed'; height: number };

// Línea lógica del ticket (texto/fila/separador/feed). Exportada para que el preview de
// MODO TEXTO la renderice en monoespaciado plano usando la MISMA fuente de contenido que
// el canvas/impresión (sin re-implementar el contenido → no diverge).
export type TicketLine = TextLine | RowLine | SepLine | FeedLine;

// ---- Derivación de las líneas desde el IR único (sin tocar el DOM) ------------
//
// emitCanvas DERIVA las TicketLine[] del MISMO TicketDoc (buildTicketModel) que alimenta a
// emitEscPos/emitTextPreview: ya NO reimplementa el contenido (eliminadas las copias locales
// de computeOrderTotal/paymentLabel/LEGAL_DISCLAIMER y los push* duplicados). El layout
// HISTÓRICO del modo imagen (roles por elemento, align propio, totales como filas etiqueta/
// valor, extras indentados) se preserva 1:1 leyendo el HINT `canvas` de cada bloque — hint
// que emitEscPos/emitTextPreview IGNORAN (byte-inerte, probado por el golden). Así el modo
// imagen no cambia su salida visual y deja de ser una cuarta fuente de contenido.

// Traduce las opciones de canvas a la TicketContentConfig del IR. El logo se carga aparte
// (loadLogo) → hasLogo:false (el bloque {logo} del doc no aplica al canvas). titleSize/
// bodySize no afectan al canvas (los roles del hint mandan el tamaño), pero se completan por
// contrato del tipo. bodyWidth=0: el wrap del canvas es por píxeles (measure/wrap), no por
// columnas → el ancho de `row` del IR no se usa aquí.
function toCanvasContentConfig(options: CanvasTicketOptions): TicketContentConfig {
  return {
    ticketType: options.ticketType,
    referencesLabel: options.referencesLabel,
    commerceName: options.commerceName,
    commerceAddress: options.commerceAddress,
    showClientPhone: options.showClientPhone,
    showCommerceName: options.showCommerceName,
    showCommerceAddress: options.showCommerceAddress,
    content: options.content,
    hasLogo: false,
    titleSizeMode: titleSize(options.textSize),
    bodySizeMode: bodySize(options.textSize),
    bodyWidth: 0,
    decoration: options.decoration,
    feedBeforeCut: 0,
    legalDisclaimer: options.legalDisclaimer,
  };
}

// Materializa el TicketDoc como lista de TicketLine[] para el canvas, honrando el hint
// `canvas` de cada bloque. PROHIBIDO agregar contenido aquí — solo materializa el doc.
export function emitCanvas(order: OrderDetail, options: CanvasTicketOptions): TicketLine[] {
  const doc = buildTicketModel(order, toCanvasContentConfig(options));
  const lines: TicketLine[] = [];
  for (const block of doc.blocks) {
    switch (block.kind) {
      case 'logo':
      case 'align':
      case 'bold':
      case 'size':
      case 'cut':
        // El logo lo carga loadLogo aparte; align/bold/size son estado de bytes (el canvas usa
        // el hint por línea); el cut no es contenido visible.
        break;
      case 'sep':
        lines.push({ kind: 'sep' });
        break;
      case 'feed':
        lines.push({ kind: 'feed', height: block.canvas?.height ?? 0 });
        break;
      case 'line': {
        const c = block.canvas;
        if (c?.skip) break;
        if (c?.asRow) {
          lines.push({
            kind: 'row',
            left: c.asRow.left,
            right: c.asRow.right,
            role: c.role ?? 'content',
            bold: c.bold ?? false,
            indent: c.asRow.indent,
          });
          break;
        }
        lines.push({
          kind: 'text',
          text: c?.text ?? block.text,
          role: c?.role ?? 'content',
          align: c?.align ?? 'left',
          bold: c?.bold ?? false,
        });
        break;
      }
      case 'row': {
        const c = block.canvas;
        lines.push({
          kind: 'row',
          left: block.left,
          right: block.right,
          role: c?.role ?? 'content',
          bold: c?.bold ?? false,
          indent: c?.asRow?.indent,
        });
        break;
      }
    }
  }
  return lines;
}

// ---- Render en canvas (toca el DOM; sólo dentro de funciones con guard) --------

// Tamaño de fuente en puntos para un rol, escalado al ancho del rollo.
function fontPx(role: TextRole, textSize: CanvasTicketOptions['textSize'], scale: number): number {
  const raw = PRINT_TEXT_SIZES_VALUES_MAP[textSize][role]; // p.ej. '17px'
  const px = parseInt(raw, 10) || 12;
  return Math.max(8, Math.round(px * scale));
}

function setFont(ctx: CanvasRenderingContext2D, sizePx: number, bold: boolean): void {
  ctx.font = `${bold ? 'bold ' : ''}${sizePx}px Arial, sans-serif`;
}

// Parte un texto en líneas que caben en `maxWidth` (wrap por palabras; si una
// palabra sola no cabe, se trocea por caracteres).
function wrapText(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string[] {
  const out: string[] = [];
  // Trocea por caracteres una palabra que no cabe en `maxWidth`: empuja a `out` los
  // segmentos completos y devuelve el último (parcial) para seguir acumulando.
  const charWrap = (word: string): string => {
    let chunk = '';
    for (const ch of word) {
      if (ctx.measureText(chunk + ch).width > maxWidth && chunk) {
        out.push(chunk);
        chunk = ch;
      } else {
        chunk += ch;
      }
    }
    return chunk;
  };
  const paragraphs = text.split('\n');
  for (const paragraph of paragraphs) {
    const words = paragraph.split(' ');
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= maxWidth) {
        line = candidate;
      } else if (!line) {
        // Palabra sola al inicio de línea que no cabe → trocear.
        line = charWrap(word);
      } else {
        // La línea actual ya tiene contenido: ciérrala y empieza con la palabra,
        // troceándola TAMBIÉN si por sí sola no cabe (antes se emitía entera y se recortaba).
        out.push(line);
        line = ctx.measureText(word).width <= maxWidth ? word : charWrap(word);
      }
    }
    out.push(line);
  }
  return out;
}

// Geometría derivada del ancho del rollo, reutilizada por medición y dibujo.
type Geometry = {
  dots: number;
  scale: number;
  contentWidth: number;
  sepWidth: number;
  lineGap: number;
  sepThickness: number;
  sepHeight: number;
};

function geometryFor(pageWidth: 58 | 80 | undefined): Geometry {
  const dots = dotsForWidth(pageWidth);
  const scale = dots / HTML_REFERENCE_WIDTH_PT;
  const lineGap = Math.round(LINE_GAP * scale);
  const sepThickness = Math.max(1, Math.round(scale));
  const contentWidth = dots - SIDE_PADDING * 2;
  // El separador usa el MISMO ancho conservador que el modo texto (columnas−1) para que
  // ambos modos se vean consistentes: ocupa separatorCols/(separatorCols+1) del ancho útil.
  const sepCols = separatorCols(pageWidth);
  const sepWidth = Math.round((contentWidth * sepCols) / (sepCols + 1));
  return {
    dots,
    scale,
    contentWidth,
    sepWidth,
    lineGap,
    sepThickness,
    sepHeight: sepThickness + lineGap,
  };
}

// Elemento ya medido y listo para pintar (filas envueltas + tamaños en puntos).
type Drawable =
  | { type: 'lines'; rows: string[]; sizePx: number; bold: boolean; align: Align; indent: number }
  | { type: 'row'; left: string[]; right: string; sizePx: number; bold: boolean; indent: number }
  | { type: 'sep' }
  | { type: 'feed'; height: number };

// Logo ya cargado y escalado (o null si no aplica/falla).
// El logo ya rasterizado a `ImageData` (píxeles leídos), NO la imagen cruda. Así se
// compone en el ticket con `putImageData` (copia píxeles ya leídos) y NUNCA propaga el
// taint al canvas del ticket.
type LoadedLogo = { data: ImageData; w: number; h: number };

// Carga el logo y lo rasteriza en un canvas AISLADO (offscreen). Prueba `getImageData`
// SOBRE ESE canvas: si el logo es cross-origin sin CORS, el probe lanza → devolvemos null
// y el ticket se imprime SIN logo, pero el canvas del ticket queda intacto (NUNCA colapsa
// a texto plano por culpa del logo). El loader robusto (fetch+createImageBitmap) recupera
// el logo en el caso común; el aislamiento es la red de seguridad.
async function loadLogo(options: CanvasTicketOptions, contentWidth: number): Promise<LoadedLogo | null> {
  if (options.showLogo === false || !options.logoUrl) return null;
  try {
    const img = await loadDrawableImage(options.logoUrl);
    const base = img.width || LOGO_MAX_SIDE;
    const ratio = Math.min(1, LOGO_MAX_SIDE / base, contentWidth / base);
    const w = Math.max(1, Math.round(base * ratio));
    const h = Math.max(1, Math.round((img.height || 1) * ratio));
    // Canvas OFFSCREEN aislado: el logo se dibuja AQUÍ, nunca en el canvas del ticket.
    const logoCanvas = document.createElement('canvas');
    logoCanvas.width = w;
    logoCanvas.height = h;
    const lctx = logoCanvas.getContext('2d');
    if (!lctx) return null;
    lctx.fillStyle = '#ffffff';
    lctx.fillRect(0, 0, w, h);
    lctx.drawImage(img, 0, 0, w, h);
    // PROBE: lanza si quedó tainted → catch → null (ticket sin logo, canvas del ticket sano).
    const data = lctx.getImageData(0, 0, w, h);
    return { data, w, h };
  } catch {
    return null;
  }
}

// Convierte una línea de texto en su `Drawable` envuelto.
function measureText(
  mctx: CanvasRenderingContext2D,
  line: TextLine,
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable {
  const sizePx = fontPx(line.role, textSize, g.scale);
  setFont(mctx, sizePx, line.bold);
  const rows = wrapText(mctx, line.text, g.contentWidth);
  return { type: 'lines', rows, sizePx, bold: line.bold, align: line.align, indent: 0 };
}

// Convierte una fila "etiqueta … valor" en su `Drawable` (izquierda envuelta).
function measureRow(
  mctx: CanvasRenderingContext2D,
  line: RowLine,
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable {
  const sizePx = fontPx(line.role, textSize, g.scale);
  setFont(mctx, sizePx, line.bold);
  const indent = Math.round((line.indent || 0) * g.scale);
  const rightWidth = mctx.measureText(line.right).width;
  const leftMaxWidth = Math.max(1, g.contentWidth - indent - rightWidth - Math.round(6 * g.scale));
  const left = wrapText(mctx, line.left, leftMaxWidth);
  return { type: 'row', left, right: line.right, sizePx, bold: line.bold, indent };
}

// Altura de un `Drawable` en puntos.
function drawableHeight(d: Drawable, g: Geometry): number {
  switch (d.type) {
    case 'lines':
      return d.rows.length * (d.sizePx + g.lineGap);
    case 'row':
      return d.left.length * (d.sizePx + g.lineGap);
    case 'sep':
      return g.sepHeight;
    case 'feed':
      return d.height;
  }
}

// Pasada de medición: convierte las líneas lógicas en `Drawable`s medidos.
function measureLines(
  mctx: CanvasRenderingContext2D,
  lines: TicketLine[],
  g: Geometry,
  textSize: CanvasTicketOptions['textSize'],
): Drawable[] {
  return lines.map((line) => {
    if (line.kind === 'sep') return { type: 'sep' };
    if (line.kind === 'feed') return { type: 'feed', height: Math.round(line.height * g.scale) };
    if (line.kind === 'text') return measureText(mctx, line, g, textSize);
    return measureRow(mctx, line, g, textSize);
  });
}

// Pinta una línea/párrafo de texto y devuelve la nueva coordenada `y`.
function paintLines(
  ctx: CanvasRenderingContext2D,
  d: Extract<Drawable, { type: 'lines' }>,
  g: Geometry,
  y: number,
): number {
  setFont(ctx, d.sizePx, d.bold);
  ctx.textAlign = d.align === 'center' ? 'center' : d.align === 'right' ? 'right' : 'left';
  const x = d.align === 'center' ? g.dots / 2 : d.align === 'right' ? g.dots - SIDE_PADDING : SIDE_PADDING;
  let ny = y;
  for (const row of d.rows) {
    ctx.fillText(row, x, ny);
    ny += d.sizePx + g.lineGap;
  }
  return ny;
}

// Pinta una fila "etiqueta … valor" y devuelve la nueva coordenada `y`.
function paintRow(
  ctx: CanvasRenderingContext2D,
  d: Extract<Drawable, { type: 'row' }>,
  g: Geometry,
  y: number,
): number {
  setFont(ctx, d.sizePx, d.bold);
  // El valor (precio) se alinea a la ÚLTIMA línea de la etiqueta cuando ésta envuelve en
  // varias líneas — si no, quedaría flotando junto a la primera con hueco debajo.
  ctx.textAlign = 'right';
  const lastLineY = y + Math.max(0, d.left.length - 1) * (d.sizePx + g.lineGap);
  ctx.fillText(d.right, g.dots - SIDE_PADDING, lastLineY);
  ctx.textAlign = 'left';
  let ny = y;
  for (const row of d.left) {
    ctx.fillText(row, SIDE_PADDING + d.indent, ny);
    ny += d.sizePx + g.lineGap;
  }
  return ny;
}

// Pinta un separador PUNTEADO (guiones con huecos), igual que el ticket de la app
// y de Electron. Se dibuja como una fila de pequeños rects para no depender del
// soporte de setLineDash y mantener el grosor exacto (g.sepThickness).
function paintSeparator(ctx: CanvasRenderingContext2D, g: Geometry, y: number): void {
  // Largo del guion y del hueco, escalados al ancho del rollo (con mínimos seguros).
  const dash = Math.max(2, Math.round(3 * g.scale));
  const gap = Math.max(2, Math.round(3 * g.scale));
  const step = dash + gap;
  const end = SIDE_PADDING + g.sepWidth;
  for (let x = SIDE_PADDING; x < end; x += step) {
    // El último guion se recorta para no rebasar el ancho de contenido.
    const w = Math.min(dash, end - x);
    ctx.fillRect(x, y, w, g.sepThickness);
  }
}

// Pasada de dibujo sobre el canvas final.
function paintDrawables(ctx: CanvasRenderingContext2D, drawables: Drawable[], g: Geometry, startY: number): void {
  let y = startY;
  for (const d of drawables) {
    if (d.type === 'sep') {
      paintSeparator(ctx, g, y);
      y += g.sepHeight;
    } else if (d.type === 'feed') {
      y += d.height;
    } else if (d.type === 'lines') {
      y = paintLines(ctx, d, g, y);
    } else {
      y = paintRow(ctx, d, g, y);
    }
  }
}

// Renderiza el ticket de un pedido a un <canvas> 2D. Devuelve el canvas, o null si
// el entorno no tiene DOM o si algo falla (para que el caller use el fallback de
// texto). Async porque puede cargar el logo.
export async function renderTicketCanvas(
  order: OrderDetail,
  options: CanvasTicketOptions,
): Promise<HTMLCanvasElement | null> {
  if (typeof document === 'undefined' || typeof Image === 'undefined') return null;
  try {
    const g = geometryFor(options.pageWidth);

    const lines = emitCanvas(order, options);

    const logo = await loadLogo(options, g.contentWidth);

    // Pasada de medición (canvas auxiliar para measureText/wrap).
    const measureCanvas = document.createElement('canvas');
    const mctx = measureCanvas.getContext('2d');
    if (!mctx) return null;
    const drawables = measureLines(mctx, lines, g, options.textSize);

    const logoBlock = logo ? logo.h + g.lineGap : 0;
    const totalHeight = SIDE_PADDING * 2 + logoBlock + drawables.reduce((sum, d) => sum + drawableHeight(d, g), 0);

    // Canvas final: fondo blanco, texto negro.
    const canvas = document.createElement('canvas');
    canvas.width = g.dots;
    canvas.height = Math.max(1, totalHeight);
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = '#000000';
    ctx.textBaseline = 'top';

    let y = SIDE_PADDING;
    if (logo) {
      // putImageData copia píxeles YA leídos (del canvas aislado) → no propaga taint.
      ctx.putImageData(logo.data, Math.round((g.dots - logo.w) / 2), y);
      y += logoBlock;
    }
    paintDrawables(ctx, drawables, g, y);

    return canvas;
  } catch {
    return null;
  }
}
